import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { User, Portfolio, Transaction } from "../types/trading";
import { supabase } from "../lib/supabase";
import { supabaseApi } from "../services/supabaseApi";

interface AuthContextType {
  user: User | null;
  portfolio: Portfolio | null;
  transactions: Transaction[];
  isLoading: boolean;
  isInitializing: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshPortfolio: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const lastFetchedUserId = useRef<string | null>(null);

  const loadAllUserData = async (userId: string) => {
    if (lastFetchedUserId.current === userId && portfolio) return;
    lastFetchedUserId.current = userId;

    try {
      const [pData, tData] = await Promise.all([
        supabaseApi.getPortfolio(userId),
        supabaseApi.getTransactions(userId),
      ]);
      setPortfolio(pData);
      setTransactions(tData);
    } catch (err) {
      console.warn("Data load error:", err);
    }
  };

  useEffect(() => {
    let mounted = true;

    const handleAuthStateChange = async (event: string, session: any) => {
      if (!mounted) return;

      if (session?.user) {
        const userId = session.user.id;

        if (lastFetchedUserId.current !== userId) {
          try {
            // 1. Fetch profile first to establish identity and role
            const profile = await supabaseApi.getUserProfile(userId);
            if (mounted) {
              setUser(profile);
              // 2. Mark initialization as done AS SOON AS the profile (and role) is known
              setIsInitializing(false);

              // 3. Load background data without blocking the UI initialization
              await loadAllUserData(userId);

              // Setup real-time listeners
              supabaseApi.subscribeToBalances(userId, () => refreshPortfolio());
              supabaseApi.subscribeToTransactions(userId, () =>
                refreshTransactions(),
              );
              supabaseApi.subscribeToUserProfile(userId, () =>
                refreshUser(),
              );
            }
          } catch (err) {
            console.error("Auth sync error:", err);
            if (mounted) setIsInitializing(false);
          }
        } else {
          // If user is the same, just ensure we aren't stuck initializing
          if (mounted) setIsInitializing(false);
        }
      } else {
        // Clear state on SIGNED_OUT
        setUser(null);
        setPortfolio(null);
        setTransactions([]);
        lastFetchedUserId.current = null;
        if (mounted) setIsInitializing(false);
      }
    };

    // 1. Get initial session — skip if on password reset page
    const isResetPage = window.location.pathname === '/reset-password';
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        if (session && !isResetPage) {
          handleAuthStateChange("INITIAL", session);
        } else {
          setIsInitializing(false);
        }
      }
    });

    // 2. Listen for changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        setUser(null);
        setPortfolio(null);
        setTransactions([]);
        lastFetchedUserId.current = null;
        setIsInitializing(false);
      } else if (event === "PASSWORD_RECOVERY") {
        // Don't authenticate — let the /reset-password page handle it
        setIsInitializing(false);
      } else {
        handleAuthStateChange(event, session);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await supabaseApi.signIn(email.trim(), password);
      // Wait briefly for onAuthStateChange to fire and profile to load
    } catch (err: any) {
      setIsLoading(false);
      throw err;
    }
  };

  const register = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    try {
      await supabaseApi.signUp(email.trim(), password, name);
    } catch (err: any) {
      setIsLoading(false);
      throw err;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await supabaseApi.signOut();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    if (!currentUser) return;
    try {
      const profile = await supabaseApi.getUserProfile(currentUser.id);
      setUser(profile);
    } catch (error) {
      console.error("User refresh failed:", error);
    }
  };

  const refreshPortfolio = async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    if (!currentUser) return;
    try {
      const data = await supabaseApi.getPortfolio(currentUser.id);
      setPortfolio(data);
    } catch (error) {
      console.error("Portfolio refresh failed:", error);
    }
  };

  const refreshTransactions = async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    if (!currentUser) return;
    try {
      const data = await supabaseApi.getTransactions(currentUser.id);
      setTransactions(data);
    } catch (error) {
      console.error("Transaction refresh failed:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        portfolio,
        transactions,
        isLoading,
        isInitializing,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin",
        login,
        register,
        logout,
        refreshPortfolio,
        refreshTransactions,
        refreshUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
